/**
 * SINGLE SOURCE OF TRUTH for experience integration configs.
 * 
 * Tap In: Consumer RSVP experience -- music/playlist integration
 * Acuity: Host connection -- scheduling/booking system link
 * 
 * Every component that displays Tap In branding MUST import from here.
 * Do NOT hardcode logo URLs, names, or descriptions elsewhere.
 */

// Tap In logo -- single source, referenced everywhere
export const TAP_IN_LOGO = "/assets/images/tap-in-logo.png";
export const TAP_IN_COVER = "https://customer-assets.emergentagent.com/job_profile-defaults/artifacts/0dnd6jdg_tapincover.png";

/**
 * Tap In verbiage varies by category context.
 * Use getTapInConfig(categoryKey) to get the right copy.
 */
const TAP_IN_CATEGORY_COPY = {
  events: {
    action: "Tap in a song",
    description: "Add your song to the event playlist",
    hostLabel: "Enable song requests for guests",
    confirmationText: "Your song has been added to the playlist",
    settingsLabel: "Tap In (song requests)",
  },
  instructors: {
    action: "Add a song",
    description: "Add a song to the workshop playlist",
    hostLabel: "Enable playlist contributions",
    confirmationText: "Your song has been added to the workshop playlist",
    settingsLabel: "Tap In (workshop playlist)",
  },
  hair: {
    action: "Set the vibe",
    description: "Add a song to your appointment playlist",
    hostLabel: "Enable music preferences for clients",
    confirmationText: "Your vibe has been set for the appointment",
    settingsLabel: "Tap In (appointment playlist)",
  },
  makeup: {
    action: "Set the vibe",
    description: "Add a song to your appointment playlist",
    hostLabel: "Enable music preferences for clients",
    confirmationText: "Your vibe has been set for the appointment",
    settingsLabel: "Tap In (appointment playlist)",
  },
  nails: {
    action: "Set the vibe",
    description: "Add a song to your appointment playlist",
    hostLabel: "Enable music preferences for clients",
    confirmationText: "Your vibe has been set for the appointment",
    settingsLabel: "Tap In (appointment playlist)",
  },
  lashes: {
    action: "Set the vibe",
    description: "Add a song to your appointment playlist",
    hostLabel: "Enable music preferences for clients",
    confirmationText: "Your vibe has been set for the appointment",
    settingsLabel: "Tap In (appointment playlist)",
  },
  culinary: {
    action: "Set the mood",
    description: "Add a song to the dining playlist",
    hostLabel: "Enable music preferences for guests",
    confirmationText: "Your song has been added to the dining playlist",
    settingsLabel: "Tap In (dining playlist)",
  },
  wellness: {
    action: "Set the tone",
    description: "Add a song to the session playlist",
    hostLabel: "Enable ambient music preferences",
    confirmationText: "Your selection has been added to the session",
    settingsLabel: "Tap In (session ambiance)",
  },
  travel: {
    action: "Set the vibe",
    description: "Add a song to the trip playlist",
    hostLabel: "Enable trip playlist contributions",
    confirmationText: "Your song has been added to the trip playlist",
    settingsLabel: "Tap In (trip playlist)",
  },
  dispensary: {
    action: "Set the vibe",
    description: "Add a song to the playlist",
    hostLabel: "Enable playlist contributions",
    confirmationText: "Your song has been added",
    settingsLabel: "Tap In (playlist)",
  },
  retail: {
    action: "Set the vibe",
    description: "Add a song to the playlist",
    hostLabel: "Enable playlist contributions",
    confirmationText: "Your song has been added",
    settingsLabel: "Tap In (playlist)",
  },
};

// Default fallback for unknown categories
const TAP_IN_DEFAULT = {
  action: "Tap in a song",
  description: "Add a song to the playlist",
  hostLabel: "Enable Tap In (playlist)",
  confirmationText: "Your song has been added to the playlist",
  settingsLabel: "Tap In (playlist)",
};

/**
 * Get Tap In configuration for a specific category.
 * Returns logo, cover image, and category-appropriate copy.
 * 
 * @param {string} categoryKey - e.g. "hair", "events", "culinary"
 * @returns {object} { logo, cover, action, description, hostLabel, confirmationText, settingsLabel }
 */
export function getTapInConfig(categoryKey) {
  const key = categoryKey?.toLowerCase?.() || "";
  const copy = TAP_IN_CATEGORY_COPY[key] || TAP_IN_DEFAULT;
  return {
    logo: TAP_IN_LOGO,
    cover: TAP_IN_COVER,
    name: "Tap In",
    ...copy,
  };
}

/**
 * Acuity integration config -- host-facing connection.
 * Not consumer-facing. Hosts link their Acuity account to sync
 * services, clients, and bookings.
 */
export const ACUITY_CONFIG = {
  name: "Acuity Scheduling",
  description: "Connect your Acuity account to sync services and bookings",
  signupUrl: "https://acuityscheduling.com",
  features: [
    "Sync appointment types as services",
    "Share client preference cards",
    "Pre-fill bookings from Acuity",
    "Calendar sync",
  ],
};
